import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Checkbox,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Tooltip,
  TextField,
  Chip,
} from '@mui/material';
import { Add, Edit, Save, Close, Delete, Phone, Calculate } from '@mui/icons-material';
import { customerReportsAPI, lookupAPI } from '../services/api';
import { Customer, CustomerMedicineData, Manufacturer } from '../types';
import CustomerMinMaxCalc from './CustomerMinMaxCalc';

interface CustomerWithRefills {
  id: number;
  customer_name: string;
  customer_number: number;
  refills: any[] | { store_id: number; medicines: any[] };
  updated_at: string;
}

const CustomerRefillsManagement: React.FC = () => {
  // State for customer list
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customersWithRefills, setCustomersWithRefills] = useState<CustomerWithRefills[]>([]);
  
  // State for add customer dialog
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // State for viewing/editing customer refills
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithRefills | null>(null);
  const [customerMedicines, setCustomerMedicines] = useState<CustomerMedicineData[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [sales60dData, setSales60dData] = useState<any[]>([]);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  
  // State for add medicine dialog
  const [addMedicineDialogOpen, setAddMedicineDialogOpen] = useState(false);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loadingManufacturers, setLoadingManufacturers] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<Manufacturer | null>(null);
  const [newMaxQuantity, setNewMaxQuantity] = useState<string>('');
  const [addingMedicine, setAddingMedicine] = useState(false);
  
  // State for edit max quantity
  const [editingMaxQty, setEditingMaxQty] = useState<number | null>(null);
  const [editingMaxQtyValue, setEditingMaxQtyValue] = useState<string>('');
  const [savingMaxQty, setSavingMaxQty] = useState<number | null>(null);
  
  // State for removing
  const [removingCustomer, setRemovingCustomer] = useState<number | null>(null);
  const [removingItem, setRemovingItem] = useState<number | null>(null);

  useEffect(() => {
    loadCustomersWithRefills();
  }, []);

  // Helper function to get current store ID
  const getCurrentStoreId = (): number | null => {
    const storeData = localStorage.getItem('selectedStore');
    if (!storeData) return null;
    const store = JSON.parse(storeData);
    return store.id;
  };

  const loadCustomersWithRefills = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const storeId = getCurrentStoreId();
      if (!storeId) {
        setError('No store selected. Please select a store first.');
        setLoading(false);
        return;
      }
      
      const response = await customerReportsAPI.getCustomersWithRefills(storeId);
      if (response.success) {
        setCustomersWithRefills(response.data);
      }
    } catch (err: any) {
      console.error('Error loading customers with refills:', err);
      setError(err.response?.data?.message || 'Failed to load customers with refills');
    } finally {
      setLoading(false);
    }
  };

  const loadAllCustomers = async () => {
    setSearchingCustomers(true);
    try {
      const customers = await lookupAPI.getCustomers();
      setAllCustomers(customers);
      setFilteredCustomers(customers);
    } catch (err: any) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleOpenAddCustomerDialog = () => {
    setAddCustomerDialogOpen(true);
    loadAllCustomers();
  };

  const handleCloseAddCustomerDialog = () => {
    setAddCustomerDialogOpen(false);
  };

  const handleAddCustomerToRefills = async (customer: Customer | null) => {
    if (!customer) return;
    
    setLoading(true);
    setError(null);

    try {
      // Fetch customer's purchase history
      const response = await customerReportsAPI.getCustomerReports(customer.customer_number);
      
      if (response.success && response.data.length > 0) {
        // Customer has purchase history, view it
        const customerWithRefills: CustomerWithRefills = {
          id: customer.id,
          customer_name: customer.customer_name,
          customer_number: customer.customer_number,
          refills: response.data,
          updated_at: new Date().toISOString()
        };
        
        handleViewCustomerRefills(customerWithRefills, response.data);
        handleCloseAddCustomerDialog();
      } else {
        setError('This customer has no purchase history. Cannot add to refills.');
      }
    } catch (err: any) {
      console.error('Error fetching customer history:', err);
      setError(err.response?.data?.message || 'Failed to fetch customer history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCustomerRefills = (customer: CustomerWithRefills, medicines?: CustomerMedicineData[]) => {
    setSelectedCustomer(customer);
    
    // Convert refills to CustomerMedicineData format
    // New structure: refills = { store_id: X, medicines: [...] }
    const refillsMedicines = Array.isArray(customer.refills) 
      ? customer.refills 
      : customer.refills?.medicines || [];
      
    const medicinesData = medicines || refillsMedicines.map((refill: any) => ({
      customer_name: customer.customer_name,
      customer_number: customer.customer_number,
      id: refill.id,
      medicine_name: refill.medicine_name,
      manufacturer_name: refill.manufacturer_name,
      pack_size: refill.pack_size,
      max_quantity: parseInt(refill.max_quantity) || refill.max_quantity
    }));
    
    setCustomerMedicines(medicinesData);
    setSelectedRows(new Set());
  };

  const handleBackToList = () => {
    setSelectedCustomer(null);
    setCustomerMedicines([]);
    setSelectedRows(new Set());
    loadCustomersWithRefills();
  };

  const handleRemoveAllRefills = async (customer: CustomerWithRefills) => {
    if (!window.confirm(`Are you sure you want to remove all refills for ${customer.customer_name}?`)) {
      return;
    }

    setRemovingCustomer(customer.id);
    setError(null);

    try {
      const storeId = getCurrentStoreId();
      if (!storeId) {
        setError('No store selected');
        setRemovingCustomer(null);
        return;
      }

      // Update with empty array to clear refills
      await customerReportsAPI.updateCustomerRefills(customer.customer_number, storeId, []);
      
      // Refresh the list
      await loadCustomersWithRefills();
    } catch (err: any) {
      console.error('Error removing customer refills:', err);
      setError(err.response?.data?.message || 'Failed to remove customer refills');
    } finally {
      setRemovingCustomer(null);
    }
  };

  const handleRemoveItem = async (manufacturerId: number) => {
    if (!selectedCustomer) return;
    
    if (!window.confirm('Are you sure you want to remove this item from refills?')) {
      return;
    }

    setRemovingItem(manufacturerId);
    setError(null);

    try {
      const storeId = getCurrentStoreId();
      if (!storeId) {
        setError('No store selected');
        setRemovingItem(null);
        return;
      }

      // Remove item from local state
      const updatedMedicines = customerMedicines.filter((med: CustomerMedicineData) => med.id !== manufacturerId);
      setCustomerMedicines(updatedMedicines);

      // Update in database
      await customerReportsAPI.updateCustomerRefills(selectedCustomer.customer_number, storeId, updatedMedicines);
      
      // If no more medicines, go back to list
      if (updatedMedicines.length === 0) {
        handleBackToList();
      }
    } catch (err: any) {
      console.error('Error removing item:', err);
      setError(err.response?.data?.message || 'Failed to remove item');
    } finally {
      setRemovingItem(null);
    }
  };

  const handleRemoveAllItems = async () => {
    if (!selectedCustomer) return;
    
    if (!window.confirm(`Are you sure you want to remove all items from ${selectedCustomer.customer_name}'s refills?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const storeId = getCurrentStoreId();
      if (!storeId) {
        setError('No store selected');
        setLoading(false);
        return;
      }

      await customerReportsAPI.updateCustomerRefills(selectedCustomer.customer_number, storeId, []);
      handleBackToList();
    } catch (err: any) {
      console.error('Error removing all items:', err);
      setError(err.response?.data?.message || 'Failed to remove all items');
    } finally {
      setLoading(false);
    }
  };

  const fetchManufacturers = async () => {
    setLoadingManufacturers(true);
    try {
      const response = await lookupAPI.getManufacturers();
      setManufacturers(response || []);
    } catch (err) {
      console.error('Error fetching manufacturers:', err);
    } finally {
      setLoadingManufacturers(false);
    }
  };

  const handleOpenAddMedicineDialog = () => {
    if (!selectedCustomer) {
      setError('Please select a customer first');
      return;
    }
    setAddMedicineDialogOpen(true);
    fetchManufacturers();
    setSelectedManufacturer(null);
    setNewMaxQuantity('');
  };

  const handleCloseAddMedicineDialog = () => {
    setAddMedicineDialogOpen(false);
    setSelectedManufacturer(null);
    setNewMaxQuantity('');
  };

  const handleAddMedicine = async () => {
    if (!selectedCustomer || !selectedManufacturer) return;

    setAddingMedicine(true);
    setError(null);

    try {
      const storeId = getCurrentStoreId();
      if (!storeId) {
        setError('No store selected');
        setAddingMedicine(false);
        return;
      }

      if (!newMaxQuantity || isNaN(parseInt(newMaxQuantity)) || parseInt(newMaxQuantity) <= 0) {
        setError('Please enter a valid max quantity');
        setAddingMedicine(false);
        return;
      }

      const maxQty = parseInt(newMaxQuantity);
      
      // Create the new item to add
      const newItem = {
        id: selectedManufacturer.id,
        medicine_name: selectedManufacturer.medicine_name,
        manufacturer_name: selectedManufacturer.manufacturer_name,
        pack_size: selectedManufacturer.pack_size || '',
        max_quantity: maxQty
      };

      // Check if item already exists
      const exists = customerMedicines.some((med: CustomerMedicineData) => med.id === selectedManufacturer.id);
      if (exists) {
        setError('This medicine already exists in the customer refills');
        setAddingMedicine(false);
        return;
      }

      // Add to local state
      const updatedMedicines = [...customerMedicines, newItem as CustomerMedicineData];
      setCustomerMedicines(updatedMedicines);

      // Update customer refills in database
      await customerReportsAPI.updateCustomerRefills(selectedCustomer.customer_number, storeId, updatedMedicines);

      handleCloseAddMedicineDialog();
    } catch (err: any) {
      console.error('Error adding medicine:', err);
      setError(err.response?.data?.message || 'Failed to add medicine');
    } finally {
      setAddingMedicine(false);
    }
  };

  const handleStartEditMaxQty = (item: CustomerMedicineData) => {
    setEditingMaxQty(item.id);
    setEditingMaxQtyValue(item.max_quantity.toString());
  };

  const handleCancelEditMaxQty = () => {
    setEditingMaxQty(null);
    setEditingMaxQtyValue('');
  };

  const handleSaveMaxQty = async (item: CustomerMedicineData) => {
    if (!selectedCustomer) return;

    const newMaxQty = parseInt(editingMaxQtyValue);
    if (isNaN(newMaxQty) || newMaxQty <= 0) {
      setError('Please enter a valid max quantity');
      return;
    }

    setSavingMaxQty(item.id);
    setError(null);

    try {
      const storeId = getCurrentStoreId();
      if (!storeId) {
        setError('No store selected');
        setSavingMaxQty(null);
        return;
      }

      // Update local state
      const updatedMedicines = customerMedicines.map((med: CustomerMedicineData) =>
        med.id === item.id ? { ...med, max_quantity: newMaxQty } : med
      );
      setCustomerMedicines(updatedMedicines);

      // Update customer refills in database
      await customerReportsAPI.updateCustomerRefills(selectedCustomer.customer_number, storeId, updatedMedicines);

      setEditingMaxQty(null);
      setEditingMaxQtyValue('');
    } catch (err: any) {
      console.error('Error updating max quantity:', err);
      setError(err.response?.data?.message || 'Failed to update max quantity');
    } finally {
      setSavingMaxQty(null);
    }
  };

  const handleSelectRow = (manufacturerId: number) => {
    setSelectedRows((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (newSet.has(manufacturerId)) {
        newSet.delete(manufacturerId);
      } else {
        newSet.add(manufacturerId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(new Set(customerMedicines.map((med: CustomerMedicineData) => med.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleExecuteMinMax = async () => {
    if (selectedRows.size === 0 || !selectedCustomer) {
      setError('Please select at least one medicine');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get selected medicines
      const selectedMedicines: CustomerMedicineData[] = customerMedicines.filter((med: CustomerMedicineData) => selectedRows.has(med.id));
      const manufacturerIds = selectedMedicines.map((med: CustomerMedicineData) => med.id);

      // ALWAYS get the CURRENT store from localStorage (in case user switched stores)
      const savedStore = localStorage.getItem('selectedStore');
      let storeId: number | null = null;
      let storeName: string = '';
      
      if (savedStore) {
        try {
          const store = JSON.parse(savedStore);
          storeId = store.id;
          storeName = store.store_name;
        } catch (error) {
          console.error('Error parsing saved store:', error);
        }
      }

      if (!storeId) {
        setError('Please select a store first from the top menu');
        setLoading(false);
        return;
      }

      // Fetch sales 60d data for selected medicines FOR THE CURRENT STORE
      console.log(`Fetching sales data for store: ${storeName} (ID: ${storeId})`);
      const salesResponse = await customerReportsAPI.getCustomerSales60d(storeId, manufacturerIds);
      
      if (salesResponse.success) {
        // Merge sales data with customer medicine data
        const enrichedData = selectedMedicines.map((med: CustomerMedicineData) => {
          const salesInfo = salesResponse.data.find((s: any) => s.manufacturer_id === med.id);
          return {
            ...med,
            sales_total_sold_60d: salesInfo?.sales_total_sold_60d || 0,
            store_name: storeName // Add store name to the data
          };
        });

        setSales60dData(enrichedData);
        setActionDialogOpen(true);
      } else {
        setError('Failed to load sales data');
      }
    } catch (err: any) {
      console.error('Error loading sales data:', err);
      setError(err.response?.data?.message || 'Failed to load sales data');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setSelectedRows(new Set());
  };

  const handleActionComplete = () => {
    setActionDialogOpen(false);
    setSelectedRows(new Set());
    // Refresh customer medicines
    if (selectedCustomer) {
      loadCustomersWithRefills();
    }
  };

  // List View
  if (!selectedCustomer) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Customer Refills Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAddCustomerDialog}
            color="success"
          >
            Add Customer to Refills
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : customersWithRefills.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No customers with refills found. Click "Add Customer to Refills" to get started.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customersWithRefills.map((customer: CustomerWithRefills) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {customer.customer_name || 'Unknown Customer'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">
                          {customer.customer_number}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${Array.isArray(customer.refills) ? customer.refills.length : (customer.refills?.medicines?.length || 0)} items`}
                        size="small" 
                        color="success" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(customer.updated_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewCustomerRefills(customer)}
                        >
                          View/Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveAllRefills(customer)}
                          disabled={removingCustomer === customer.id}
                          startIcon={removingCustomer === customer.id ? <CircularProgress size={16} /> : <Delete />}
                        >
                          Remove
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add Customer Dialog */}
        <Dialog open={addCustomerDialogOpen} onClose={handleCloseAddCustomerDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add Customer to Refills</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Search for a customer to add to the refills program. The customer must have existing purchase history.
              </Typography>
              
              <Autocomplete<Customer>
                options={filteredCustomers}
                getOptionLabel={(option: Customer) => 
                  `${option.customer_name || 'Unknown'} - ${option.customer_number || ''}`
                }
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    placeholder="Type to search customer by name or number"
                    variant="outlined"
                  />
                )}
                onChange={(event: any, newValue: Customer | null) => handleAddCustomerToRefills(newValue)}
                onInputChange={(event: any, newInputValue: string) => {
                  if (newInputValue.trim()) {
                    const filtered = allCustomers.filter(
                      (customer: Customer) =>
                        (customer.customer_name && customer.customer_name.toLowerCase().includes(newInputValue.toLowerCase())) ||
                        (customer.customer_number && customer.customer_number.toString().includes(newInputValue))
                    );
                    setFilteredCustomers(filtered);
                  } else {
                    setFilteredCustomers(allCustomers);
                  }
                }}
                loading={searchingCustomers || loading}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddCustomerDialog}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Detail View
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Button onClick={handleBackToList} sx={{ mb: 1 }}>
            ← Back to List
          </Button>
          <Typography variant="h5" component="h2">
            {selectedCustomer.customer_name} - Refills ({customerMedicines.length} items)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleRemoveAllItems}
            disabled={loading || customerMedicines.length === 0}
          >
            Remove All Items
          </Button>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleOpenAddMedicineDialog}
          >
            Add Medicine
          </Button>
          <Button 
            variant="contained" 
            onClick={handleExecuteMinMax}
            disabled={selectedRows.size === 0 || loading || customerMedicines.length === 0}
            startIcon={loading ? <CircularProgress size={16} /> : <Calculate />}
          >
            {loading ? 'Loading...' : `Calculate Min-Max (${selectedRows.size} selected)`}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                  <Checkbox
                    indeterminate={selectedRows.size > 0 && selectedRows.size < customerMedicines.length}
                    checked={customerMedicines.length > 0 && selectedRows.size === customerMedicines.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>Medicine Name</TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>Manufacturer</TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>Pack Size</TableCell>
                <TableCell align="right" sx={{ bgcolor: 'background.paper' }}>Max Quantity</TableCell>
                <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customerMedicines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No medicines found. Click "Add Medicine" to add medicines for this customer.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                customerMedicines.map((row: CustomerMedicineData) => (
                  <TableRow 
                    key={row.id} 
                    hover 
                    selected={selectedRows.has(row.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.has(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                      />
                    </TableCell>
                    <TableCell>{row.medicine_name}</TableCell>
                    <TableCell>{row.manufacturer_name}</TableCell>
                    <TableCell>{row.pack_size}</TableCell>
                    <TableCell align="right">
                      {editingMaxQty === row.id ? (
                        <TextField
                          type="number"
                          size="small"
                          value={editingMaxQtyValue}
                          onChange={(e) => setEditingMaxQtyValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveMaxQty(row);
                            } else if (e.key === 'Escape') {
                              handleCancelEditMaxQty();
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Save">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveMaxQty(row)}
                                    disabled={savingMaxQty === row.id}
                                  >
                                    {savingMaxQty === row.id ? <CircularProgress size={16} /> : <Save />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton size="small" onClick={handleCancelEditMaxQty}>
                                    <Close />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          autoFocus
                          sx={{ width: '120px' }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Typography>{row.max_quantity}</Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleStartEditMaxQty(row)}
                            disabled={savingMaxQty === row.id}
                            sx={{ opacity: 0.5 }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(row.id)}
                        disabled={removingItem === row.id}
                      >
                        {removingItem === row.id ? <CircularProgress size={16} /> : <Delete />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Medicine Dialog */}
      <Dialog open={addMedicineDialogOpen} onClose={handleCloseAddMedicineDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Medicine</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Autocomplete<Manufacturer>
              options={manufacturers}
              getOptionLabel={(option: Manufacturer) =>
                `${option.manufacturer_name} - ${option.medicine_name} (${option.pack_size || 'N/A'})`
              }
              value={selectedManufacturer}
              onChange={(event: any, newValue: Manufacturer | null) => setSelectedManufacturer(newValue)}
              loading={loadingManufacturers}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  label="Search Medicine by Name"
                  required
                  placeholder="Type to search..."
                />
              )}
            />

            <TextField
              label="Max Quantity"
              type="number"
              value={newMaxQuantity}
              onChange={(e) => setNewMaxQuantity(e.target.value)}
              fullWidth
              required
              helperText="Enter the maximum quantity the customer has purchased"
              inputProps={{ min: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMedicineDialog} disabled={addingMedicine}>
            Cancel
          </Button>
          <Button
            onClick={handleAddMedicine}
            variant="contained"
            disabled={!selectedManufacturer || !newMaxQuantity || addingMedicine}
            startIcon={addingMedicine ? <CircularProgress size={20} /> : <Add />}
          >
            Add Medicine
          </Button>
        </DialogActions>
      </Dialog>

      {/* Min-Max Calculator Dialog */}
      {selectedCustomer && sales60dData.length > 0 && (
        <CustomerMinMaxCalc
          open={actionDialogOpen}
          onClose={handleCloseActionDialog}
          selectedItems={sales60dData}
          customerName={selectedCustomer.customer_name}
          customerNumber={selectedCustomer.customer_number}
          onComplete={handleActionComplete}
          storeName={sales60dData[0]?.store_name}
        />
      )}
    </Box>
  );
};

export default CustomerRefillsManagement;

